import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Action } from '@ngrx/store';
import {
  createAdminOffMarketProperty,
  createAdminOffMarketPropertyFailure,
  createAdminOffMarketPropertySuccess,
  deleteAdminOffMarketProperty,
  deleteAdminOffMarketPropertyDocument,
  deleteAdminOffMarketPropertyDocumentFailure,
  deleteAdminOffMarketPropertyDocumentSuccess,
  deleteAdminOffMarketPropertyFailure,
  deleteAdminOffMarketPropertySuccess,
  generateAdminOffMarketPropertyShareLink,
  generateAdminOffMarketPropertyShareLinkFailure,
  generateAdminOffMarketPropertyShareLinkSuccess,
  getAdminOffMarketProperties,
  getAdminOffMarketPropertiesFailure,
  getAdminOffMarketPropertiesSuccess,
  getAdminOffMarketPropertyDetails,
  getAdminOffMarketPropertyDetailsFailure,
  getAdminOffMarketPropertyDetailsSuccess,
  updateAdminOffMarketProperty,
  updateAdminOffMarketPropertyFailure,
  updateAdminOffMarketPropertySuccess,
  uploadAdminOffMarketPropertyDocuments,
  uploadAdminOffMarketPropertyDocumentsFailure,
  uploadAdminOffMarketPropertyDocumentsSuccess,
} from './off-market-properties.action';
import { setSharedData } from '../Authentication/authentication.actions';
import { OffMarketPropertiesService } from 'src/app/core/services/off-market-properties/off-market-properties.service';
import { CustomSnackbarComponent } from 'src/app/shared/custom-snackbar/custom-snackbar.component';
import { environment } from 'src/environments/environment.prod';
import { Clipboard } from '@angular/cdk/clipboard';

@Injectable()
export class OffMarketPropertiesEffects {
  constructor(
    private actions$: Actions,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
    private offMarketPropertiesService: OffMarketPropertiesService,
    private clipboard: Clipboard
  ) {}

  getAdminOffMarketPropertiesEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAdminOffMarketProperties),
      switchMap((action) =>
        this.offMarketPropertiesService
          .getAdminOffMarketProperties(action.data)
          .pipe(
            mergeMap((res) => [
              getAdminOffMarketPropertiesSuccess({ data: res }),
              setSharedData({ data: res }),
            ]),
            catchError((error) => {
              return of(getAdminOffMarketPropertiesFailure({ error }));
            })
          )
      )
    );
  });

  getAdminOffMarketPropertyDetailsEffect$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getAdminOffMarketPropertyDetails),
        switchMap((action) =>
          this.offMarketPropertiesService
            .getAdminOffMarketPropertyDetails(action.id)
            .pipe(
              mergeMap((res) => [
                getAdminOffMarketPropertyDetailsSuccess({ data: res }),
              ]),
              catchError((error) => {
                return of(getAdminOffMarketPropertyDetailsFailure({ error }));
              })
            )
        )
      );
    }
  );

  createAdminOffMarketPropertyEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(createAdminOffMarketProperty),
      switchMap((action) =>
        this.offMarketPropertiesService
          .createAdminOffMarketProperty(action.data)
          .pipe(
            tap((result) => {
              if (result) {
                this.dialog.closeAll();
                this.snackBar.openFromComponent(CustomSnackbarComponent, {
                  data: {
                    title: `Off-market property created successfully.`,
                    type: 'success',
                  },
                  panelClass: 'success',
                  duration: 3000,
                  horizontalPosition: 'right',
                });
                this.router.navigate([
                  'admin-offmarket-properties',
                  'details',
                  result.id,
                ]);
              }
            }),
            mergeMap((res) => [
              createAdminOffMarketPropertySuccess({ data: res }),
            ]),
            catchError((error) => {
              return of(createAdminOffMarketPropertyFailure({ error }));
            })
          )
      )
    );
  });

  updateAdminOffMarketPropertyEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateAdminOffMarketProperty),
      switchMap((action) =>
        this.offMarketPropertiesService
          .updateAdminOffMarketProperty(action.data)
          .pipe(
            mergeMap((res) => [
              updateAdminOffMarketPropertySuccess({ data: res }),
            ]),
            catchError((error) => {
              return of(updateAdminOffMarketPropertyFailure({ error }));
            })
          )
      )
    );
  });

  deleteAdminOffMarketPropertyEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteAdminOffMarketProperty),
      switchMap((action) =>
        this.offMarketPropertiesService
          .deleteAdminOffMarketProperty(action.id)
          .pipe(
            mergeMap((res) => [
              deleteAdminOffMarketPropertySuccess({ data: res }),
              getAdminOffMarketProperties({ data: action.data }),
            ]),
            catchError((error) => {
              return of(deleteAdminOffMarketPropertyFailure({ error }));
            })
          )
      )
    );
  });

  uploadAdminOffMarketPropertyDocumentsEffect$: Observable<Action> =
    createEffect(() => {
      return this.actions$.pipe(
        ofType(uploadAdminOffMarketPropertyDocuments),
        switchMap((action) =>
          this.offMarketPropertiesService
            .uploadAdminOffMarketPropertyDocuments(action.data)
            .pipe(
              mergeMap((res) => [
                uploadAdminOffMarketPropertyDocumentsSuccess({ data: res }),
              ]),
              catchError((error) => {
                return of(
                  uploadAdminOffMarketPropertyDocumentsFailure({ error })
                );
              })
            )
        )
      );
    });

  deleteAdminOffMarketPropertyDocumentEffect$: Observable<Action> =
    createEffect(() => {
      return this.actions$.pipe(
        ofType(deleteAdminOffMarketPropertyDocument),
        switchMap((action) =>
          this.offMarketPropertiesService
            .deleteAdminOffMarketPropertyDocument({
              propertyId: action.propertyId,
              imageId: action.documentId,
            })
            .pipe(
              mergeMap((res) => [
                deleteAdminOffMarketPropertyDocumentSuccess({ data: res }),
              ]),
              catchError((error) => {
                return of(
                  deleteAdminOffMarketPropertyDocumentFailure({ error })
                );
              })
            )
        )
      );
    });

  generateAdminOffMarketPropertyShareLinkEffect$: Observable<Action> =
    createEffect(() => {
      return this.actions$.pipe(
        ofType(generateAdminOffMarketPropertyShareLink),
        switchMap((action) =>
          this.offMarketPropertiesService
            .generateAdminOffMarketPropertyShareLink(action.propertyId)
            .pipe(
              tap((result) => {
                if (result.token) {
                  this.snackBar.openFromComponent(CustomSnackbarComponent, {
                    data: {
                      title: `Share link generated & copied to clipboard successfully.`,
                      type: 'success',
                    },
                    panelClass: 'success',
                    duration: 5000,
                    horizontalPosition: 'right',
                  });

                  this.clipboard.copy(
                    environment.webUrl + 'off-market-property/' + result.token
                  );
                }
              }),
              mergeMap((res) => [
                generateAdminOffMarketPropertyShareLinkSuccess({ data: res }),
              ]),
              catchError((error) => {
                return of(
                  generateAdminOffMarketPropertyShareLinkFailure({ error })
                );
              })
            )
        )
      );
    });
}
